<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import { useRoute } from "vue-router";
import dayjs from "dayjs";
import apiClient from "@/apiClient";
import FuelCardUsageChart from "@/components/FuelCardUsageChart.vue";
import useDebouncedFunction from "@/composables/useDebouncedFuncion";

const route = useRoute();

const fuelCard = ref(null);
const movements = ref([]);
const usages = ref([]);
const vehicles = ref([]);
const staffMembers = ref([]);

const loadingCard = ref(true);
const loadingMovements = ref(false);
const loadingUsages = ref(false);
const loadingVehicles = ref(false);
const loadingStaff = ref(false);

const cardSaving = ref(false);
const movementSaving = ref(false);
const cardReady = ref(false);
const pendingCardSave = ref(false);
const lastSavedCardSignature = ref("");

const cardError = ref("");
const movementError = ref("");
const toast = reactive({
  visible: false,
  message: "",
  tone: "success",
});
let toastTimeout = null;

const cardForm = reactive({
  card_name: "",
  card_number: "",
  expiry_date: "",
  active: true,
});

const movementForm = reactive({
  vehicle_id: "",
  moved_at: dayjs().format("YYYY-MM-DDTHH:mm"),
  staff_id: "",
  notes: "",
});

const fuelCardId = computed(() => route.params.id);

const currencyFormatter = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 2,
});

const formatDateTime = (value) =>
  value ? dayjs(value).format("DD MMM YYYY HH:mm") : "—";
const formatDate = (value) =>
  value ? dayjs(value).format("DD MMM YYYY") : "—";

const normalizeDateInput = (value) => {
  if (!value) {
    return "";
  }

  const parsed = dayjs(value);
  if (parsed.isValid()) {
    return parsed.format("YYYY-MM-DD");
  }

  return String(value).slice(0, 10);
};

const formatFuelType = (value) => {
  if (value === "premium diesel") {
    return "Premium Diesel";
  }
  if (value === "diesel") {
    return "Diesel";
  }
  if (value === "petrol") {
    return "Petrol";
  }
  return value || "—";
};

const formatQuantity = (value) =>
  value === null || value === undefined ? "—" : `${Number(value).toFixed(2)} L`;

const formatMoney = (value) =>
  value === null || value === undefined
    ? "—"
    : currencyFormatter.format(Number(value));

const usageChart = computed(() => {
  const items = Array.isArray(usages.value) ? usages.value : [];
  const buckets = new Map();

  items.forEach((usage) => {
    if (!usage?.drawn_at) {
      return;
    }

    const key = dayjs(usage.drawn_at).startOf("month").format("YYYY-MM");
    const current = buckets.get(key) || { quantity: 0, amount: 0 };
    current.quantity += Number(usage.quantity ?? 0);
    current.amount += Number(usage.net_amount ?? 0);
    buckets.set(key, current);
  });

  const keys = [...buckets.keys()].sort();

  return {
    labels: keys.map((key) => dayjs(`${key}-01`).format("MMM YY")),
    quantities: keys.map((key) =>
      Number((buckets.get(key)?.quantity ?? 0).toFixed(2)),
    ),
    amounts: keys.map((key) =>
      Number((buckets.get(key)?.amount ?? 0).toFixed(2)),
    ),
  };
});

const usageTotals = computed(() => ({
  transactions: Array.isArray(usages.value) ? usages.value.length : 0,
  quantity: (Array.isArray(usages.value) ? usages.value : []).reduce(
    (sum, usage) => sum + Number(usage?.quantity ?? 0),
    0,
  ),
  amount: (Array.isArray(usages.value) ? usages.value : []).reduce(
    (sum, usage) => sum + Number(usage?.net_amount ?? 0),
    0,
  ),
}));

const showToast = (message, tone = "success") => {
  toast.message = message;
  toast.tone = tone;
  toast.visible = true;
  if (toastTimeout) {
    clearTimeout(toastTimeout);
  }
  toastTimeout = setTimeout(() => {
    toast.visible = false;
  }, 3000);
};

const buildCardPayload = () => {
  const cardName = cardForm.card_name?.trim() || "";
  const cardNumber = cardForm.card_number?.trim().toUpperCase() || "";
  const expiryDate = normalizeDateInput(cardForm.expiry_date);

  if (!fuelCard.value || !cardName || !cardNumber) {
    return null;
  }

  return {
    card_name: cardName,
    card_number: cardNumber,
    expiry_date: expiryDate || null,
    active: Boolean(cardForm.active),
  };
};

const getCardSignature = (payload) => JSON.stringify(payload || {});

const applyCardToForm = () => {
  if (!fuelCard.value) {
    return;
  }
  cardForm.card_name = fuelCard.value.card_name || "";
  cardForm.card_number = (fuelCard.value.card_number || "").toUpperCase();
  cardForm.expiry_date = normalizeDateInput(fuelCard.value.expiry_date);
  cardForm.active = Boolean(fuelCard.value.active);
};

const loadFuelCard = async () => {
  loadingCard.value = true;
  cardReady.value = false;
  cardError.value = "";
  try {
    const { data } = await apiClient.get(`/api/fuel-card/${fuelCardId.value}`);
    fuelCard.value = data;
    applyCardToForm();
    lastSavedCardSignature.value = getCardSignature(buildCardPayload());
  } catch (error) {
    cardError.value = "Unable to load fuel card.";
  } finally {
    loadingCard.value = false;
    await nextTick();
    cardReady.value = true;
  }
};

const loadMovements = async () => {
  loadingMovements.value = true;
  movementError.value = "";
  try {
    const { data } = await apiClient.get(
      `/api/fuel-card/${fuelCardId.value}/movement`,
    );
    movements.value = Array.isArray(data) ? data : [];
  } catch (error) {
    movementError.value = "Unable to load movement log.";
    movements.value = [];
  } finally {
    loadingMovements.value = false;
  }
};

const loadUsages = async () => {
  loadingUsages.value = true;
  try {
    const { data } = await apiClient.get(
      `/api/fuel-card/${fuelCardId.value}/usage`,
    );
    usages.value = Array.isArray(data) ? data : [];
  } catch (error) {
    usages.value = [];
  } finally {
    loadingUsages.value = false;
  }
};

const loadVehicles = async () => {
  loadingVehicles.value = true;
  try {
    const { data } = await apiClient.get("/api/vehicle", {
      params: {
        per_page: 250,
        active: "all",
      },
    });
    const payload = data?.data || data || [];
    vehicles.value = Array.isArray(payload)
      ? [...payload].sort((a, b) =>
          (a.registrationNumber || "").localeCompare(
            b.registrationNumber || "",
            undefined,
            { sensitivity: "base", numeric: true },
          ),
        )
      : [];
  } catch {
    vehicles.value = [];
  } finally {
    loadingVehicles.value = false;
  }
};

const loadStaff = async () => {
  loadingStaff.value = true;
  try {
    const { data } = await apiClient.get("/api/staff", {
      params: {
        page: "all",
      },
    });
    staffMembers.value = Array.isArray(data) ? data : [];
  } catch {
    staffMembers.value = [];
  } finally {
    loadingStaff.value = false;
  }
};

const saveCardDetails = async () => {
  if (!cardReady.value || !fuelCard.value) {
    return;
  }

  const payload = buildCardPayload();
  if (!payload) {
    cardError.value = "Card name and card number are required.";
    return;
  }

  const signature = getCardSignature(payload);
  if (signature === lastSavedCardSignature.value) {
    return;
  }

  if (cardSaving.value) {
    pendingCardSave.value = true;
    return;
  }

  cardSaving.value = true;
  cardError.value = "";
  try {
    const { data } = await apiClient.put(
      `/api/fuel-card/${fuelCard.value.id}`,
      payload,
    );
    fuelCard.value = data;
    applyCardToForm();
    lastSavedCardSignature.value = getCardSignature(buildCardPayload());
    showToast("Fuel card changes saved.");
  } catch (error) {
    cardError.value =
      error?.response?.data?.message ?? "Unable to save fuel card.";
    showToast(cardError.value, "error");
  } finally {
    cardSaving.value = false;
    if (pendingCardSave.value) {
      pendingCardSave.value = false;
      saveCardDetails();
    }
  }
};

const { debouncedFunction: debouncedSaveCard, disableTimer } =
  useDebouncedFunction(saveCardDetails, 800);

const addMovement = async () => {
  if (!movementForm.vehicle_id) {
    movementError.value = "Please select a vehicle.";
    return;
  }
  if (!movementForm.moved_at) {
    movementError.value = "Moved date/time is required.";
    return;
  }

  movementSaving.value = true;
  movementError.value = "";
  try {
    await apiClient.post(`/api/fuel-card/${fuelCardId.value}/movement`, {
      vehicle_id: Number(movementForm.vehicle_id),
      moved_at: movementForm.moved_at,
      staff_id: movementForm.staff_id ? Number(movementForm.staff_id) : null,
      notes: movementForm.notes?.trim() || null,
    });
    movementForm.vehicle_id = "";
    movementForm.staff_id = "";
    movementForm.moved_at = dayjs().format("YYYY-MM-DDTHH:mm");
    movementForm.notes = "";
    await Promise.all([loadFuelCard(), loadMovements()]);
  } catch (error) {
    movementError.value =
      error?.response?.data?.message ?? "Unable to add movement.";
  } finally {
    movementSaving.value = false;
  }
};

onMounted(async () => {
  await Promise.all([
    loadFuelCard(),
    loadMovements(),
    loadUsages(),
    loadVehicles(),
    loadStaff(),
  ]);
});

watch(
  cardForm,
  () => {
    if (!cardReady.value) {
      return;
    }

    debouncedSaveCard();
  },
  { deep: true },
);

onBeforeUnmount(() => {
  disableTimer();
  if (toastTimeout) {
    clearTimeout(toastTimeout);
  }
});
</script>

<template>
  <div class="space-y-6">
    <div
      class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
    >
      <router-link to="/fuel-cards" class="btn w-full text-center md:w-auto">
        Back to Fuel Cards
      </router-link>
      <div class="text-sm text-slate-500 dark:text-slate-400">
        {{ cardSaving ? "Saving changes..." : "Changes save automatically" }}
      </div>
    </div>

    <div v-if="cardError" class="box text-sm text-red-600">
      {{ cardError }}
    </div>

    <div v-if="loadingCard" class="box">Loading fuel card...</div>

    <template v-else-if="fuelCard">
      <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <div
          class="rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
        >
          <div
            class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400"
          >
            Card Name
          </div>
          <div class="text-lg font-semibold mt-1">
            {{ fuelCard.card_name || "—" }}
          </div>
          <div class="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {{ fuelCard.card_number || "—" }}
          </div>
        </div>
        <div
          class="rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
        >
          <div
            class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400"
          >
            Expiry
          </div>
          <div class="text-lg font-semibold mt-1">
            {{ formatDate(fuelCard.expiry_date) }}
          </div>
        </div>
        <div
          class="rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
        >
          <div
            class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400"
          >
            Current Vehicle
          </div>
          <div class="text-2xl font-semibold mt-1">
            {{ fuelCard.current_vehicle?.registrationNumber || "Unassigned" }}
          </div>
        </div>
        <div
          class="rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
        >
          <div
            class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400"
          >
            Moved There
          </div>
          <div class="text-lg font-semibold mt-1">
            {{ formatDateTime(fuelCard.current_vehicle_moved_at) }}
          </div>
        </div>
        <div
          class="rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
        >
          <div
            class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400"
          >
            Assigned Staff
          </div>
          <div class="text-lg font-semibold mt-1">
            {{ fuelCard.assigned_staff?.contact?.name || "—" }}
          </div>
        </div>
      </div>

      <section class="box">
        <h2 class="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Fuel Card Details
        </h2>
        <div class="grid gap-4 md:grid-cols-4">
          <label class="block md:col-span-2">
            <span>Card Name</span>
            <input
              v-model="cardForm.card_name"
              type="text"
              class="mt-1 w-full"
            />
          </label>
          <label class="block md:col-span-2">
            <span>Card Number</span>
            <input
              v-model="cardForm.card_number"
              type="text"
              class="mt-1 w-full uppercase"
            />
          </label>
          <label class="block md:col-span-2">
            <span>Expiry Date</span>
            <input
              v-model="cardForm.expiry_date"
              type="date"
              class="input mt-1 w-full [color-scheme:light] dark:[color-scheme:dark]"
            />
          </label>
          <label class="inline-flex items-center gap-2 md:self-end">
            <input v-model="cardForm.active" type="checkbox" />
            <span>{{ cardForm.active ? "Active" : "Inactive" }}</span>
          </label>
        </div>
      </section>

      <section class="grid gap-4">
        <div class="box space-y-4">
          <h2 class="text-lg font-semibold text-slate-900 dark:text-white">
            Record Movement
          </h2>
          <label class="block">
            <span>Vehicle</span>
            <select
              v-model="movementForm.vehicle_id"
              class="input mt-1 w-full"
              :disabled="loadingVehicles || movementSaving"
            >
              <option value="">
                {{ loadingVehicles ? "Loading vehicles..." : "Select vehicle" }}
              </option>
              <option
                v-for="vehicle in vehicles"
                :key="vehicle.id"
                :value="vehicle.id"
              >
                {{ vehicle.registrationNumber }}
              </option>
            </select>
          </label>
          <label class="block">
            <span>Moved At</span>
            <input
              v-model="movementForm.moved_at"
              type="datetime-local"
              class="input mt-1 w-full [color-scheme:light] dark:[color-scheme:dark]"
            />
          </label>
          <label class="block">
            <span>Assigned Staff (optional)</span>
            <select
              v-model="movementForm.staff_id"
              class="input mt-1 w-full"
              :disabled="loadingStaff || movementSaving"
            >
              <option value="">
                {{ loadingStaff ? "Loading staff..." : "No staff assigned" }}
              </option>
              <option
                v-for="staff in staffMembers"
                :key="staff.id"
                :value="staff.id"
              >
                {{ staff.contact?.name || `Staff #${staff.id}` }}
              </option>
            </select>
          </label>
          <label class="block">
            <span>Notes (optional)</span>
            <textarea
              v-model="movementForm.notes"
              rows="2"
              class="input mt-1 w-full"
            />
          </label>
          <p v-if="movementError" class="text-sm text-red-500">
            {{ movementError }}
          </p>
          <button
            class="btn accent"
            :disabled="movementSaving"
            @click.prevent="addMovement"
          >
            {{ movementSaving ? "Saving..." : "Add Movement" }}
          </button>
        </div>
      </section>

      <section class="box p-0 overflow-hidden">
        <header
          class="px-4 py-3 border-b border-slate-200 dark:border-slate-700"
        >
          <h2 class="font-semibold text-slate-900 dark:text-white">
            Movement Log
          </h2>
        </header>
        <div class="overflow-auto">
          <table class="w-full text-sm">
            <thead
              class="text-xs uppercase text-slate-500 bg-slate-50 dark:bg-slate-900/30"
            >
              <tr>
                <th class="p-3 text-left">Moved At</th>
                <th class="p-3 text-left">Vehicle</th>
                <th class="p-3 text-left">Assigned Staff</th>
                <th class="p-3 text-left">Recorded By</th>
                <th class="p-3 text-left">Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loadingMovements">
                <td colspan="5" class="p-4 text-center text-slate-500">
                  Loading movement log...
                </td>
              </tr>
              <tr
                v-for="movement in movements"
                :key="movement.id"
                class="border-t border-slate-200 dark:border-slate-800"
              >
                <td class="p-3">{{ formatDateTime(movement.moved_at) }}</td>
                <td class="p-3">
                  {{ movement.vehicle?.registrationNumber || "—" }}
                </td>
                <td class="p-3">{{ movement.staff?.contact?.name || "—" }}</td>
                <td class="p-3">{{ movement.moved_by_user?.name || "—" }}</td>
                <td class="p-3">{{ movement.notes || "—" }}</td>
              </tr>
              <tr v-if="!loadingMovements && !movements.length">
                <td colspan="5" class="p-4 text-center text-slate-500">
                  No movement history yet.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="box p-0 overflow-hidden">
        <header
          class="px-4 py-3 border-b border-slate-200 dark:border-slate-700"
        >
          <h2 class="font-semibold text-slate-900 dark:text-white">
            Usage Trend
          </h2>
        </header>
        <div class="p-4 space-y-4">
          <div class="grid gap-4 md:grid-cols-3">
            <div
              class="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/30"
            >
              <div class="text-xs uppercase tracking-wide text-slate-500">
                Total Transactions
              </div>
              <div
                class="mt-1 text-2xl font-semibold text-slate-900 dark:text-white"
              >
                {{ usageTotals.transactions }}
              </div>
            </div>
            <div
              class="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/30"
            >
              <div class="text-xs uppercase tracking-wide text-slate-500">
                Total Volume
              </div>
              <div
                class="mt-1 text-2xl font-semibold text-slate-900 dark:text-white"
              >
                {{ formatQuantity(usageTotals.quantity) }}
              </div>
            </div>
            <div
              class="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/30"
            >
              <div class="text-xs uppercase tracking-wide text-slate-500">
                Total Spend
              </div>
              <div
                class="mt-1 text-2xl font-semibold text-slate-900 dark:text-white"
              >
                {{ formatMoney(usageTotals.amount) }}
              </div>
            </div>
          </div>

          <div v-if="usageChart.labels.length">
            <FuelCardUsageChart
              :labels="usageChart.labels"
              :quantities="usageChart.quantities"
              :amounts="usageChart.amounts"
            />
          </div>
          <div
            v-else
            class="rounded-xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-slate-700"
          >
            Usage history will appear here once fuel drawings have been recorded
            or imported.
          </div>
        </div>
      </section>

      <section class="box p-0 overflow-hidden">
        <header
          class="px-4 py-3 border-b border-slate-200 dark:border-slate-700"
        >
          <h2 class="font-semibold text-slate-900 dark:text-white">
            Usage Log
          </h2>
        </header>
        <div class="overflow-auto">
          <table class="w-full text-sm">
            <thead
              class="text-xs uppercase text-slate-500 bg-slate-50 dark:bg-slate-900/30"
            >
              <tr>
                <th class="p-3 text-left">Date Drawn</th>
                <th class="p-3 text-left">Fuel Type</th>
                <th class="p-3 text-left">Quantity</th>
                <th class="p-3 text-left">Net Amount</th>
                <th class="p-3 text-left">Recorded By</th>
                <th class="p-3 text-left">Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loadingUsages">
                <td colspan="6" class="p-4 text-center text-slate-500">
                  Loading usage log...
                </td>
              </tr>
              <tr
                v-for="usage in usages"
                :key="usage.id"
                class="border-t border-slate-200 dark:border-slate-800"
              >
                <td class="p-3">{{ formatDateTime(usage.drawn_at) }}</td>
                <td class="p-3">{{ formatFuelType(usage.fuel_type) }}</td>
                <td class="p-3">{{ formatQuantity(usage.quantity) }}</td>
                <td class="p-3">{{ formatMoney(usage.net_amount) }}</td>
                <td class="p-3">{{ usage.recorded_by_user?.name || "—" }}</td>
                <td class="p-3">{{ usage.notes || "—" }}</td>
              </tr>
              <tr v-if="!loadingUsages && !usages.length">
                <td colspan="6" class="p-4 text-center text-slate-500">
                  No usage records yet.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>

    <div
      v-if="toast.visible"
      class="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium shadow-xl"
      :class="
        toast.tone === 'error'
          ? 'bg-rose-600 text-white'
          : 'bg-emerald-600 text-white'
      "
    >
      <svg
        v-if="toast.tone !== 'error'"
        class="h-4 w-4 text-white"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M5 13l4 4L19 7"
        />
      </svg>
      <svg
        v-else
        class="h-4 w-4 text-white"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <span>{{ toast.message }}</span>
    </div>
  </div>
</template>
